import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, first, switchMap } from 'rxjs/operators';
import { AuthService } from '@auth0/auth0-angular';
import { Auth0Service } from '../services/auth0.service';
import { UserProfileService } from '../services/user-profile.service';
import { LOGIN_PATH } from '../../../../apps/values/path';
import { NotificationService } from '@progress/kendo-angular-notification';

@Injectable({
  providedIn: 'root',
})
export class IsUserWithAdminPrivilegesGuard implements CanActivate {
  constructor(
    private _authService: AuthService,
    private _auth0Service: Auth0Service,
    private _userProfileService: UserProfileService,
    private _router: Router,
    private _notificationService: NotificationService
  ) {}

  canActivate(
    activatedRouteSnapshot: ActivatedRouteSnapshot,
    routerStateSnapshot: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    // Get Auth0 user and check their profile in database
    return this._auth0Service.user$.pipe(
      first(),
      switchMap((user) => {
        if (!user?.sub) {
          // No Auth0 user - redirect to login
          void this._router.navigate(['/', LOGIN_PATH]);
          return of(false);
        }

        // Get user profile from database
        return this._userProfileService.getUserProfile(user.sub).pipe(
          map((userProfile) => {
            if (!userProfile) {
              // No profile found - logout
              this._authService.logout({
                logoutParams: {
                  returnTo: window.location.origin + `/${LOGIN_PATH}`,
                },
              });
              return false;
            }

            // Check if user has admin privileges (no user_group_code means admin)
            if (!userProfile.user_group_code) {
              // No user_group_code means admin user
              return true;
            } else {
              // Has user_group_code means therapist user - deny access
              this._notificationService.show({
                content:
                  "You do not have the correct permissions/privileges to access this application. Please contact your organisation's administrator for further information",
                hideAfter: 3000,
                animation: { type: 'slide', duration: 300 },
                position: { horizontal: 'center', vertical: 'top' },
                type: { style: 'error', icon: true },
              });

              // Logout from Auth0
              this._authService.logout({
                logoutParams: {
                  returnTo: window.location.origin + `/${LOGIN_PATH}`,
                },
              });

              return false;
            }
          })
        );
      })
    );
  }
}
